import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import useAuth from '../authentication/useAuth';
import ConnectionChangeContext from './ConnectionChangeContext';
// There's no need to check whether the investor is authenticated
// The useRequest hook already validates and handles that
const ConnectionChangeProvider = ({ children }) => {
    const auth = useAuth();
    const [connectionChangeData, setConnectionChangeData] = useState({
        error: undefined,
        hasConnectionChangeError: false
    });
    // Tracks the batch of requests currently in-flight across every useRequest
    // instance. Refs (not state) so the zero-check below is synchronous and
    // can't race with a request that starts in the same tick.
    const pendingRequestsCountRef = useRef(0);
    const hadConnectionChangeInBatchRef = useRef(false);
    const hadUnauthorizedInBatchRef = useRef(false);
    // Mirrors the ambient hasConnectionChangeError in a ref so registerRequestEnd
    // (memoized with a stable identity) always reads the latest value instead of
    // a stale closure. The session must stay protected while the renew-session
    // modal is open even if that flag was raised by a batch that already settled.
    const hasConnectionChangeErrorRef = useRef(connectionChangeData.hasConnectionChangeError);
    hasConnectionChangeErrorRef.current = connectionChangeData.hasConnectionChangeError;
    const registerRequestStart = useCallback(() => {
        pendingRequestsCountRef.current += 1;
    }, []);
    const registerRequestEnd = useCallback((hadConnectionChange, hadUnauthorized) => {
        hadConnectionChangeInBatchRef.current = hadConnectionChangeInBatchRef.current
            || hadConnectionChange;
        hadUnauthorizedInBatchRef.current = hadUnauthorizedInBatchRef.current || hadUnauthorized;
        pendingRequestsCountRef.current -= 1;
        if (pendingRequestsCountRef.current > 0)
            return;
        // The batch has fully settled: decide once whether the session should be
        // cleared. The session stays protected if this batch saw a connection
        // change OR the ambient flag is already true (e.g. a previous batch
        // raised it and the renew-session modal is still open).
        const isSessionProtected = hadConnectionChangeInBatchRef.current
            || hasConnectionChangeErrorRef.current;
        if (!isSessionProtected && hadUnauthorizedInBatchRef.current) {
            auth.clearSession();
        }
        hadConnectionChangeInBatchRef.current = false;
        hadUnauthorizedInBatchRef.current = false;
    }, [auth]);
    const connectionChangeContext = useMemo(() => ({
        error: connectionChangeData?.error,
        hasConnectionChangeError: connectionChangeData?.hasConnectionChangeError,
        setConnectionChangeData,
        registerRequestStart,
        registerRequestEnd
    }), [connectionChangeData, registerRequestStart, registerRequestEnd]);
    return (_jsx(ConnectionChangeContext.Provider, { value: connectionChangeContext, children: children }));
};
export default ConnectionChangeProvider;
